import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, HelpCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useAuth } from '../../hooks/useAuth';

// Feature onboarding content configuration
const FEATURE_CONTENT = {
  oneOnOnes: {
    title: '1:1 Meetings',
    icon: '👥',
    steps: [
      {
        title: 'Track Your Relationships',
        description: 'Add the people you have regular 1:1s with - direct reports, managers, or peers. Set their meeting day to stay organized.',
        tip: 'Click "Add Relationship" to get started'
      },
      {
        title: 'Prepare Topics & Action Items',
        description: 'Throughout the week, add topics you want to discuss. During your meeting, check them off and create action items with due dates.',
        tip: 'Topics carry over week-to-week until discussed'
      },
      {
        title: 'Meeting Mode',
        description: 'When your 1:1 starts, enter Meeting Mode for a focused view. Take notes, track decisions, and assign action items in real-time.',
        tip: 'Use the "Start Meeting" button when you\'re ready'
      },
      {
        title: 'Review History',
        description: 'Access past sessions to see what was discussed, decisions made, and action items completed. Great for performance reviews!',
        tip: 'Past sessions are read-only but always accessible'
      }
    ]
  },
  goals: {
    title: 'Goals & Projects',
    icon: '🎯',
    steps: [
      {
        title: 'Set Your Goals',
        description: 'Define what you want to achieve. Goals can be quarterly OKRs, annual objectives, or ongoing projects you\'re working on.',
        tip: 'Be specific - "Increase sales 20%" beats "Do better"'
      },
      {
        title: 'Link Tasks to Goals',
        description: 'Connect your daily tasks to goals to see progress. When you complete tasks, your goal progress updates automatically.',
        tip: 'Use the goal field when creating tasks'
      },
      {
        title: 'Track Progress',
        description: 'Update goal status as you make progress. Mark milestones complete and add notes to document your journey.',
        tip: 'Review goals weekly to stay on track'
      }
    ]
  },
  habits: {
    title: 'Habits & Routines',
    icon: '✨',
    steps: [
      {
        title: 'Build Your Routine',
        description: 'Add daily, weekly, or monthly habits you want to track. These are recurring tasks that keep your work and life on track.',
        tip: 'Start small - 3-5 habits is better than 20'
      },
      {
        title: 'Check Off Daily',
        description: 'Each day, check off habits as you complete them. The progress bar shows how you\'re doing at a glance.',
        tip: 'Habits reset automatically at the start of each period'
      },
      {
        title: 'Schedule Weekly Items',
        description: 'Weekly habits can be scheduled for specific days. They\'ll appear in your daily view on those days.',
        tip: 'Great for things like "Friday reporting" or "Monday planning"'
      }
    ]
  },
  meetings: {
    title: 'Meetings',
    icon: '📅',
    steps: [
      {
        title: 'See Your Calendar',
        description: 'View all your meetings in a calendar view. Click any day to see what\'s scheduled and prepare accordingly.',
        tip: 'Connect Google Calendar in Settings to sync your events'
      },
      {
        title: 'Create Meetings',
        description: 'Add meetings manually or create recurring series. Include agendas, attendees, and related documents.',
        tip: 'Use templates for meetings you have often'
      },
      {
        title: 'Meeting Prep',
        description: 'Click any meeting to see details, add notes, and link related tasks. Never walk into a meeting unprepared.',
        tip: 'Add prep tasks directly from the meeting view'
      }
    ]
  },
  schedule: {
    title: 'Schedule & Time Blocking',
    icon: '⏰',
    steps: [
      {
        title: 'Plan Your Day',
        description: 'See your calendar events alongside your tasks. Visualize how your day is structured and where you have focus time.',
        tip: 'Drag tasks onto time blocks to schedule them'
      },
      {
        title: 'Create Time Blocks',
        description: 'Block dedicated time for deep work, meetings, or breaks. Time blocks help protect your focus and set boundaries.',
        tip: 'Try the "2-hour rule" - block 2 hours for your most important work'
      },
      {
        title: 'Balance Your Load',
        description: 'See task counts per time block to avoid overcommitting. If a block is overloaded, redistribute or reschedule.',
        tip: 'Aim for 3-5 tasks per time block maximum'
      }
    ]
  },
  tasks: {
    title: 'Tasks & GTD',
    icon: '✅',
    steps: [
      {
        title: 'Capture Everything',
        description: 'Get thoughts out of your head and into your Inbox. Don\'t organize yet - just capture quickly.',
        tip: 'Use quick add or press "N" to add tasks fast'
      },
      {
        title: 'Process Your Inbox',
        description: 'Go through inbox items one by one. For each: Is it actionable? If yes, decide when to do it. If no, delete or save for later.',
        tip: 'Aim for Inbox Zero daily'
      },
      {
        title: 'Work Your Today List',
        description: 'Your Today list is sacred - only items you\'re committed to completing today. Be realistic about what fits.',
        tip: 'If you can\'t do it today, move it to Scheduled or Someday'
      },
      {
        title: 'Use Waiting For',
        description: 'Track items you\'re blocked on. Add who you\'re waiting for and when to follow up.',
        tip: 'Review waiting items in your Daily Review'
      }
    ]
  },
  dailyReview: {
    title: 'Daily Review',
    icon: '🌅',
    steps: [
      {
        title: 'The 5-Minute Ritual',
        description: 'The Daily Review is your end-of-day habit that keeps you ahead of work. Spend 5 minutes each evening to set up tomorrow for success.',
        tip: 'Schedule this at the same time daily - consistency is key'
      },
      {
        title: 'Process Your Inbox',
        description: 'Clear out any items that landed in your inbox today. Decide what each item is and where it belongs.',
        tip: 'Don\'t overthink - quick decisions are better than perfect ones'
      },
      {
        title: 'Review Tomorrow\'s Calendar',
        description: 'Look at what\'s scheduled for tomorrow. Do you need to prepare for any meetings? Are there conflicts to resolve?',
        tip: 'Add prep tasks for important meetings'
      },
      {
        title: 'Plan Tomorrow\'s Tasks',
        description: 'Select 3-5 tasks to commit to for tomorrow. Be realistic about what you can accomplish given your calendar.',
        tip: 'If it doesn\'t fit, move it to Scheduled - don\'t overload'
      },
      {
        title: 'Check Follow-ups',
        description: 'Review your Waiting For items. Anyone you need to nudge? Any follow-up dates due tomorrow?',
        tip: 'A quick follow-up email now saves you time later'
      }
    ]
  },
  weeklyReview: {
    title: 'Weekly Review',
    icon: '📋',
    steps: [
      {
        title: 'The GTD Secret Weapon',
        description: 'The Weekly Review is what makes GTD actually work. It\'s your chance to get clear, get current, and get creative about your work and life.',
        tip: 'Block 30-60 minutes - Friday afternoon or Sunday evening works well'
      },
      {
        title: 'Get Clear',
        description: 'Process all your inboxes to zero - email, physical inbox, notes, voicemails. Capture any new tasks or ideas that surface.',
        tip: 'This is about capturing, not doing - just get it into your system'
      },
      {
        title: 'Get Current',
        description: 'Review your calendar (past week for loose ends, next 2 weeks for prep needed). Review all your task lists and projects.',
        tip: 'Ask: Is this still relevant? What\'s the next action?'
      },
      {
        title: 'Review Goals',
        description: 'Check in on your goals and projects. Are you making progress? Do priorities need to shift? Any projects stalled?',
        tip: 'Update goal status and add any new tasks needed'
      },
      {
        title: 'Get Creative',
        description: 'With a clear mind, think about the bigger picture. Any new ideas? Someday/Maybe items to activate? Creative projects to start?',
        tip: 'This is where breakthroughs happen - don\'t skip it!'
      }
    ]
  }
};

export default function FeatureOnboarding({ feature, children }) {
  const { user, userProfile } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenFeature, setHasSeenFeature] = useState(true); // Default to true to prevent flash

  const content = FEATURE_CONTENT[feature];

  // Check if user has seen this feature's onboarding
  useEffect(() => {
    if (userProfile) {
      const seen = userProfile.discoveredFeatures?.[feature] === true;
      setHasSeenFeature(seen);
      // Auto-show if not seen
      if (!seen) {
        setShowOnboarding(true);
      }
    }
  }, [userProfile, feature]);

  // Mark feature as discovered
  const markAsDiscovered = async () => {
    if (!user?.uid) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        [`discoveredFeatures.${feature}`]: true,
        updatedAt: new Date()
      });
      setHasSeenFeature(true);
    } catch (error) {
      console.error('Error marking feature as discovered:', error);
    }
  };

  const handleClose = () => {
    setShowOnboarding(false);
    setCurrentStep(0);
    if (!hasSeenFeature) {
      markAsDiscovered();
    }
  };

  const handleNext = () => {
    if (currentStep < content.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleShowOnboarding = () => {
    setCurrentStep(0);
    setShowOnboarding(true);
  };

  if (!content) return children;

  const step = content.steps[currentStep];

  return (
    <>
      {/* Help button - always visible */}
      <button
        onClick={handleShowOnboarding}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[150] p-2.5 bg-surface-card border border-border rounded-full shadow-lg hover:bg-surface-hover hover:border-accent/50 transition-all group"
        title={`Learn about ${content.title}`}
      >
        <HelpCircle size={20} className="text-text-secondary group-hover:text-accent transition-colors" />
      </button>

      {/* Onboarding Modal - rendered via portal to escape stacking contexts */}
      {showOnboarding && createPortal(
        <>
          {/* Backdrop - covers everything including sidebar */}
          <div
            className="fixed inset-0 z-[9998] bg-black/80"
            onClick={handleClose}
          />

          {/* Modal - centered in viewport */}
          <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-surface-elevated">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{content.icon}</span>
                <div>
                  <h2 className="text-base font-semibold text-text-primary">{content.title}</h2>
                  <p className="text-xs text-text-tertiary">
                    Step {currentStep + 1} of {content.steps.length}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-lg hover:bg-surface-hover text-text-tertiary hover:text-text-primary transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 py-3 bg-surface-elevated/50">
              {content.steps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep
                      ? 'bg-accent w-6'
                      : idx < currentStep
                        ? 'bg-accent/50'
                        : 'bg-border'
                  }`}
                />
              ))}
            </div>

            {/* Content */}
            <div className="p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {step.title}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed mb-4">
                {step.description}
              </p>

              {/* Tip box */}
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
                <p className="text-xs text-accent font-medium">
                  💡 {step.tip}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-border bg-surface-elevated">
              <button
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={14} />
                Back
              </button>

              <button
                onClick={handleClose}
                className="text-xs text-text-tertiary hover:text-text-secondary transition-all"
              >
                Skip tutorial
              </button>

              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-4 py-1.5 bg-accent text-white rounded-lg text-xs font-medium hover:bg-accent/90 transition-all"
              >
                {currentStep === content.steps.length - 1 ? 'Get Started' : 'Next'}
                {currentStep < content.steps.length - 1 && <ChevronRight size={14} />}
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {children}
    </>
  );
}
